import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Theme,
  useTheme,
  Select,
  FormControl,
  MenuItem,
  Tooltip,
  Button,
} from '@mui/material';
import customworkspaceStyles from './ccf-custom-workspace.styles';
import {
  CcfHeader,
  CcfReturnTabIcon,
  CcfTypography,
  CcfViewInNewWindowIcon,
  CcfOpenNewTabIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  getActivePersistentPanel,
  getCustomWorkspaces,
  globalActions,
} from '../global.app.slice';
import {
  LocalStorageHelper,
  PersistentPanel,
  StorageKeys,
} from '@nice-ccf/core-sdk';

/**
 * Custom workspace props
 */
interface CcfCustomWorkspaceProps {
  /**
   * @remarks - Currently active custom workspace
   */
  activeCustomSpaceIframe?: PersistentPanelFrame;
}

/**
 * Declared individual objects containing label and iframe element
 */
export interface PersistentPanelFrame {
  /**
   * @remarks - label of custom workspace
   */
  label: string;
  /**
   * @remarks - Custom workspace iframe element
   */
  frame: () => JSX.Element;
}

/**
 * Custom Workpsace Container which includes header and iframe
 * @example - <CcfcustomWorkspace />
 * @returns
 */
const CcfCustomWorkspace = (props: CcfCustomWorkspaceProps) => {
  const dispatch = useDispatch();
  const theme: Theme = useTheme();
  const styles = customworkspaceStyles(theme);
  const customWorkspaces = useSelector(getCustomWorkspaces);
  const activecustomWorkspace = useSelector(getActivePersistentPanel);
  const customWorkspacePanelLs = JSON.parse(
    localStorage.getItem('customWorkspacePanel') || '[]'
  );

  const activeCustomWorkspaceLS = useRef();
  const activeCustomWorkspaceLabelLS =
    LocalStorageHelper.getItem(StorageKeys.ACTIVE_CUSTOMWORKSPACE) &&
    LocalStorageHelper.getItem(StorageKeys.ACTIVE_CUSTOMWORKSPACE, true)
      .persistentPanelLabel;
  const [isWindowPoppedout, setWindowPopoutStatus] = useState(false);
  const [cwWindow, setCwWindow] = useState<Window>();
  const [customWorkspaceLabel, setCustomWorkspaceLabel] = useState<string>(
    activecustomWorkspace.persistentPanelLabel ?? activeCustomWorkspaceLabelLS
  );
  const [translate] = useTranslator();

  /**
   * Event handler to handle the onclick state for the label to reflect activelabel url on iframe
   * Internal details - set active custom workspace
   * @param args - none,
   * @example setActiveCustomWorkspace()
   */
  const setActiveCustomWorkspace = (item: PersistentPanel) => {
    LocalStorageHelper.setItem(StorageKeys.ACTIVE_CUSTOMWORKSPACE, item);
    setWindowPopoutStatus(false);
    dispatch(globalActions.setActivePersistentPanel(item));
  };

  /**
   * Event handler to handle the action for the open in new tab button
   * Internal details - Pop out to new browser tab
   * @param args - none,
   * @example openInNewTab()
   */
  const openInNewTab = () => {
    setWindowPopoutStatus(true);
    const opnedWindow = window.open(
      activecustomWorkspace.persistentPanelURI,
      activecustomWorkspace.persistentPanelLabel
    );
    opnedWindow?.focus();
    opnedWindow && setCwWindow(opnedWindow);
  };

  /**
   * Event handler to handle the action for return to agent view
   * Internal details - Pop in to agent view
   * @param args - none,
   * @example returnToAgentView()
   */
  const returnToAgentView = () => {
    setWindowPopoutStatus(false);
    cwWindow?.close();
  };

  useEffect(() => {
    setCustomWorkspaceLabel(
      activecustomWorkspace.persistentPanelLabel ?? activeCustomWorkspaceLabelLS
    );
    if (Object.keys(activecustomWorkspace).length > 0) {
      setWindowPopoutStatus(false);
    }
  }, [activecustomWorkspace]);

  useEffect(() => {
    // activeCustomWorkspaceLS.current = LocalStorageHelper.getItem(
    //   StorageKeys.ACTIVE_CUSTOMWORKSPACE
    // );
    // activeCustomWorkspaceLS.current &&
    setActiveCustomWorkspace(customWorkspaces[0]);
    localStorage.removeItem('customWorkspacePanel');
  }, []);

  useEffect(() => {
    if (customWorkspacePanelLs.length) {
      dispatch(globalActions.setPersistentPanels(customWorkspacePanelLs));
      setActiveCustomWorkspace(customWorkspacePanelLs[0]);
    }
  }, [customWorkspacePanelLs]);

  return (
    <Box data-testid="customWorkspace-container" sx={styles.iframeContainer}>
      <Box component="section" sx={styles.customWorkspaceHeader}>
        <CcfHeader RightIcon={false} />
        <Grid container direction={'row'} justifyContent={'space-between'}>
          <Grid>
            <FormControl
              variant="standard"
              sx={styles.customDropdown}
              size="small"
            >
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                data-testid="customworkspace-menu"
                value={customWorkspaceLabel}
                label={translate('customWorkspace')}
                disableUnderline
              >
                {customWorkspaces.map((item, index) => (
                  <MenuItem
                    value={item.persistentPanelLabel}
                    data-testid={`customworkspace-menu-item-${index}`}
                    onClick={() => setActiveCustomWorkspace(item)}
                  >
                    {item.persistentPanelLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            data-testid="pop-out-pop-in-buttons"
            sx={styles.openinnewtabIcon}
          >
            {isWindowPoppedout ? (
              <Tooltip title={translate('returnToAgentView')} arrow>
                <Button sx={styles.openinnewTabButton}>
                  <CcfReturnTabIcon
                    data-testid="return-to-agent-view"
                    onClick={() => returnToAgentView()}
                  />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={translate('openInNewTab')} arrow>
                <Button sx={styles.openinnewTabButton}>
                  <CcfOpenNewTabIcon
                    data-testid="open-in-new-tab"
                    onClick={() => openInNewTab()}
                  />
                </Button>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Box>
      {isWindowPoppedout ? (
        <Box
          data-testid="open-in-new-tab-window"
          sx={styles.openInNewWindowContainer}
        >
          <Box sx={styles.openInNewWindowIcon}>
            <CcfViewInNewWindowIcon />
          </Box>
          <Box sx={styles.openInNewWindowContent}>
            <Box>
              <CcfTypography className="messageText">
                {translate('viewingInBrowserMessage')}
              </CcfTypography>
              <Box className="link_text">
                <CcfTypography
                  data-testid="click-to-open-in-new-tab"
                  className="link"
                  variant="h5"
                  tabIndex={0}
                  onClick={() => openInNewTab()}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') openInNewTab();
                  }}
                >
                  {translate('clickHere')}
                </CcfTypography>
                <CcfTypography variant="h5">
                  {translate('viewInBrowser')}
                </CcfTypography>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        // : props.activeCustomSpaceIframe
        //   ? <iframe
        //   title={translate('customWorkspace')}
        //   src={activeCustomWorkspaceLS.current && JSON.parse(activeCustomWorkspaceLS.current).persistentPanelURI}
        //   width={'100%'}
        //   className="customworkspace-iframe"
        // />
        <iframe
          title={translate('customWorkspace')}
          security='none'
          referrerPolicy='unsafe-url'
          src={
            activecustomWorkspace && activecustomWorkspace.persistentPanelURI
          }
          width={'100%'}
          className="customworkspace-iframe"
        />
      )}
    </Box>
  );
};

export default CcfCustomWorkspace;
